import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { List, X } from '@phosphor-icons/react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    
    handleResize();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { name: 'About', id: 'about-section' },
    { name: 'Services', id: 'services-section' },
    { name: 'Testimonials', id: 'testimonials-section' },
    { name: 'Contact', id: 'enquiry-section' }
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`top-0 left-0 right-0 z-50 transition-all duration-300 ${isMobile ? 'absolute' : 'fixed'}`}
        style={{
          background: isScrolled || isMobileMenuOpen ? 'rgba(5, 5, 5, 1)' : 'transparent',
          boxShadow: isScrolled ? '0 4px 20px rgba(0, 0, 0, 0.5)' : 'none'
        }}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="cursor-pointer"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setIsMobileMenuOpen(false);
              }}
            >
              <img
                src="/white.png"
                alt="Soccial Symphony"
                className="h-10 md:h-12 w-auto"
                style={{ 
                  filter: 'saturate(1.2) contrast(1.1) drop-shadow(0 0 8px rgba(0, 0, 0, 0.8))'
                }}
              />
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-base font-medium transition-all duration-300 relative group"
                  style={{ color: '#e8e8e8ff' }}
                  data-testid={`nav-link-${item.id}`}
                >
                  <span className="group-hover:text-white transition-colors">{item.name}</span>
                  <span
                    className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                    style={{ background: '#F59E0B' }}
                  />
                </button>
              ))}
            </div>

            {/* Desktop CTA Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('enquiry-section')}
              className="hidden md:block px-6 py-2.5 rounded-full font-semibold relative overflow-hidden group"
              style={{
                background: '#e8e8e8ff',
                color: '#000',
                border: '2px solid #000'
              }}
              data-testid="nav-cta-button"
            >
              <span className="absolute inset-0 bg-gradient-to-b from-[#F59E0B] to-[#F59E0B] transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out"></span>
              <span className="relative z-10">Get Started</span>
            </motion.button>

            {/* Mobile Hamburger Button */}
            <button
              className="md:hidden p-2 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{ color: '#e8e8e8ff' }}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? (
                <X size={28} weight="bold" />
              ) : (
                <List size={28} weight="bold" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden"
              style={{ background: 'rgba(5, 5, 5, 0.98)' }}
            >
              <div className="px-4 py-6 space-y-4">
                {navItems.map((item, index) => (
                  <div
                    key={item.id}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setTimeout(() => {
                        const element = document.getElementById(item.id);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    className="block w-full text-left text-lg font-medium py-3 px-4 rounded-xl transition-all cursor-pointer active:scale-95"
                    style={{ 
                      color: '#FAFAFA',
                      background: 'rgba(44, 43, 42, 0.1)',
                      border: '1px solid rgba(0, 0, 0, 0.2)'
                    }}
                    data-testid={`mobile-nav-link-${item.id}`}
                  >
                    {item.name}
                  </div>
                ))}
                
                {/* Mobile CTA */}
                <div
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setTimeout(() => {
                      const element = document.getElementById('enquiry-section');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                  className="block w-full text-center text-lg font-bold py-4 px-4 rounded-xl mt-4 cursor-pointer active:scale-95"
                  style={{ 
                    background: '#e8e8e8ff',
                    color: '#000'
                  }}
                  data-testid="mobile-cta-button"
                >
                  Get Started
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navbar;
